import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, X, Send, Minimize2, ChevronDown } from 'lucide-react';

const ChatWidget = () => {
  const { api, isAuthenticated, isFullyAuthenticated, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [subject, setSubject] = useState('');
  const [showStart, setShowStart] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  // Load existing session
  useEffect(() => {
    if (!isFullyAuthenticated || !open) return;
    const loadSession = async () => {
      try {
        const res = await api.get('/chat/sessions');
        const openSession = res.data.find(s => s.status === 'open');
        if (openSession) {
          setSession(openSession);
          setShowStart(false);
          loadMessages(openSession._id);
        }
      } catch {}
    };
    loadSession();
  }, [isFullyAuthenticated, open]);

  const loadMessages = async (sessionId) => {
    try {
      const res = await api.get(`/chat/sessions/${sessionId}/messages`);
      setMessages(res.data);
    } catch {}
  };

  // Polling for new messages
  useEffect(() => {
    if (!session || !open) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const poll = () => {
      const lastMsg = messages[messages.length - 1];
      const since = lastMsg?.created_at || '';
      api.get(`/chat/sessions/${session._id}/messages${since ? `?since=${since}` : ''}`)
        .then(res => {
          if (res.data.length > 0) {
            if (since) {
              setMessages(prev => [...prev, ...res.data]);
            } else {
              setMessages(res.data);
            }
          }
        })
        .catch(() => {});
    };
    pollRef.current = setInterval(poll, 4000);
    return () => clearInterval(pollRef.current);
  }, [session, open, messages.length]);

  const startChat = async () => {
    try {
      const res = await api.post('/chat/sessions', { subject: subject || 'Általános kérdés' });
      setSession(res.data);
      setShowStart(false);
      setMessages([]);
    } catch {}
  };

  const sendMessage = async () => {
    if (!input.trim() || !session || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    // Optimistic update
    const optimistic = {
      _id: 'temp-' + Date.now(),
      sender_type: 'user',
      sender_name: user?.username || '?',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);

    try {
      const res = await api.post(`/chat/sessions/${session._id}/messages`, { content: text });
      setMessages(prev => prev.map(m => m._id === optimistic._id ? res.data : m));
    } catch {
      setMessages(prev => prev.filter(m => m._id !== optimistic._id));
      setInput(text);
    }
    setSending(false);
  };

  const closeChat = async () => {
    if (!session) return;
    try {
      await api.put(`/chat/sessions/${session._id}/close`);
      setSession(null);
      setMessages([]);
      setShowStart(true);
    } catch {}
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const timeStr = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isFullyAuthenticated) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-orange-600 hover:bg-orange-700 rounded-full shadow-xl shadow-orange-500/30 flex items-center justify-center text-white transition-all hover:scale-105 active:scale-95"
          data-testid="chat-widget-btn"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] sm:w-[400px] h-[520px] bg-black/95 border border-orange-500/20 rounded-2xl shadow-2xl shadow-black/60 flex flex-col overflow-hidden backdrop-blur-xl" data-testid="chat-window">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-orange-600/10 border-b border-orange-500/20">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-orange-400" />
              <span className="font-semibold text-sm text-white">Támogatás</span>
              {session && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">Online</span>}
            </div>
            <div className="flex items-center gap-1">
              {session && (
                <button onClick={closeChat} className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors" title="Chat lezárása" data-testid="close-chat-btn">
                  <X className="h-4 w-4 text-red-400" />
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="minimize-chat-btn">
                <Minimize2 className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          {showStart ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-orange-400" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Segíthetünk?</h3>
              <p className="text-gray-500 text-sm mb-6">Írd le a kérdésedet és csapatunk hamarosan válaszol!</p>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Téma (opcionális)"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 outline-none focus:border-orange-500/50 mb-3"
                data-testid="chat-subject-input"
              />
              <button
                onClick={startChat}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2.5 rounded-xl transition-colors"
                data-testid="start-chat-btn"
              >
                Chat indítása
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                {messages.length === 0 && (
                  <div className="text-center text-gray-600 text-sm py-8">
                    Írd le a kérdésedet, és csapatunk hamarosan válaszol!
                  </div>
                )}
                {messages.map(m => (
                  <div key={m._id} className={`flex ${m.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] ${m.sender_type === 'user' ? 'order-2' : ''}`}>
                      {m.sender_type === 'staff' && (
                        <p className="text-[10px] text-orange-400 mb-0.5 ml-1">{m.sender_name}</p>
                      )}
                      <div className={`px-3.5 py-2.5 rounded-2xl text-sm break-words ${
                        m.sender_type === 'user'
                          ? 'bg-orange-600 text-white rounded-br-md'
                          : 'bg-white/10 text-gray-200 rounded-bl-md'
                      }`} data-testid={`chat-msg-${m._id}`}>
                        {m.content}
                      </div>
                      <p className={`text-[10px] text-gray-600 mt-0.5 ${m.sender_type === 'user' ? 'text-right mr-1' : 'ml-1'}`}>
                        {timeStr(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-white/10">
                <div className="flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Írd ide az üzeneted..."
                    rows={1}
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-gray-500 outline-none focus:border-orange-500/50 resize-none max-h-24"
                    data-testid="chat-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || sending}
                    className="p-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-xl transition-colors flex-shrink-0"
                    data-testid="chat-send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
